/**
 * KAN-889: form of the decision-maker contact (name, role, phone, Telegram,
 * email). Rendered only while editing, so the card without the edit right
 * works outside QueryClientProvider. A 400 keeps the form open with the
 * typed values and the server text under the field.
 */
import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LEAD_CONTACT_FIELDS,
  leadContactsErrors,
  leadContactsFormValues,
  useLeadContactsSave,
  type LeadContactField,
} from '@/hooks/useLeadContactsSave';
import type { LeadContact, LeadContactsPayload } from '@/types/client';

const LABELS: Record<LeadContactField, string> = {
  name: 'Имя',
  role: 'Должность',
  phone: 'Телефон',
  telegram: 'Telegram',
  email: 'Email',
};

const INPUT_TYPES: Record<LeadContactField, string> = {
  name: 'text',
  role: 'text',
  phone: 'tel',
  telegram: 'text',
  email: 'email',
};

interface LeadContactsFormProps {
  clientId: string;
  contact?: LeadContact;
  onClose: () => void;
}

export function LeadContactsForm({ clientId, contact, onClose }: LeadContactsFormProps) {
  const [values, setValues] = useState<LeadContactsPayload>(() => leadContactsFormValues(contact));
  const save = useLeadContactsSave(clientId, contact, onClose);
  const errors = leadContactsErrors(save.error);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.submit(values);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-3" data-testid="lead-contacts-form">
      {LEAD_CONTACT_FIELDS.map((field) => {
        const id = `lead-contacts-${field}-${clientId}`;
        const error = errors[field];
        return (
          <div key={field} className="space-y-1">
            <Label htmlFor={id}>{LABELS[field]}</Label>
            <Input
              id={id}
              type={INPUT_TYPES[field]}
              value={values[field]}
              onChange={(e) => setValues((prev) => ({ ...prev, [field]: e.target.value }))}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${id}-error` : undefined}
              data-testid={`lead-contacts-${field}`}
            />
            {error && (
              <p id={`${id}-error`} className="text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );
      })}
      {errors.form && <p className="text-sm text-red-600">{errors.form}</p>}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
          Сохранить
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onClose} disabled={save.isPending}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
